import './ApiKeyView.css'
import { Button } from "../Common/Elements/Button";
import { useTranslation } from "react-i18next";
import React, { useContext, useEffect, useState } from "react";
import { NotificationsContext } from "../../App";
import { PopupContentProps } from "../Common/Elements/Popup";
import { SmallPopup } from "../Common/Elements/SmallPopup";
import { loading } from "../../communication/wrapped";
import { Resources } from "../../types/explyt";
import { getResources, postGenerateAPIKey } from "../../communication/communication";
import { LoadingCircle } from "../Common/Loading/LoadingPlaceholder";
import { SetRootNavigationSelected } from "../Common/Navigation/Navigation";
import {useUtils} from "@tma.js/sdk-react";

function RecreateKeyWarningPopup(props: PopupContentProps<{ generate: () => void }>) {
    const { t } = useTranslation();

    return <div className="flex-column small-gap">
        <span className="font-white fw-600" style={{ fontSize: "1.8rem" }}>
            {t("areYouSureYouWantToGenerateNewKey")}
        </span>
        <span className="font-gray medium-font">
            {t("____newKeyWarningText")}
        </span>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }} className="small-margin-top">
            <Button
                text={t("cancel")}
                onClick={props.closePopup}
                backgroundColor={"white"}
                textColor={"black"}
            />
            <Button
                text={t("generate")}
                onClick={() => { props.closePopup(); props.data.generate() }}
                backgroundColor={"var(--color-main)"}
                textColor={"black"}
            />
        </div>
    </div>
}

function fallbackCopyTextToClipboard(text: string) {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const success = document.execCommand('copy');
    if (!success) throw new Error();
    document.body.removeChild(textArea);
}

export function ApiKeyView() {
    const [copied, setCopied] = useState(false);
    const [{ data }, setResources] = useState(loading<Resources>(null!));
    const { t } = useTranslation();
    const [showRecreatePopup, setShowRecreatePopup] = useState(false);

    const addNotification = useContext(NotificationsContext);
    const utils = useUtils()

    useEffect(() => {
        getResources().then(setResources)
    }, []);

    const copyKey = () => {
        if (!data?.explyt_api_token) {
            addNotification(t("keyNotLoaded"))
            return;
        }
        const key = data.explyt_api_token
        navigator.clipboard.writeText(key).then(() => {
            addNotification(t("copied"));
            setCopied(true);
        }).catch(() => {
            try {
                    fallbackCopyTextToClipboard(key);
                addNotification(t("copied"));
                setCopied(true);
            } catch {
                addNotification(t("errorOccurred"));
            }
        });
    }

    const setNavigation = useContext(SetRootNavigationSelected)

    useEffect(() => {
        if (data?.explyt_api_token === "") {
            setNavigation(0);
            postGenerateAPIKey().then(r => setResources(x => {
                const copy = { ...x };
                copy.data = r.data;
                copy.statusCode = r.statusCode;
                addNotification(t("done"));
                return copy;
            })).then(copyKey)
        }
    }, [addNotification, t, data]); // eslint-disable-line


    const generateKey = () => {
        setResources(loading<Resources>(null!));
        postGenerateAPIKey().then(r => setResources(x => {
            const copy = { ...x };
            copy.data = r.data;
            copy.statusCode = r.statusCode;
            addNotification(t("done"));
            setCopied(false);
            return copy;
        }))
    }


    const recreatePopup = <SmallPopup
        isOpen={showRecreatePopup}
        setIsOpen={setShowRecreatePopup}
        contentFactory={d => <RecreateKeyWarningPopup {...d} />}
        data={{ generate: generateKey }}
        zIndex={10}
    />

    console.log(data)

    const copyButton = <Button
        text={copied ? t("copied") : t("copy")}
        onClick={!copied ? copyKey : () => { }}
        backgroundColor={copied ? "var(--progress-bar-button-bg)" : "var(--color-main)"}
        textColor={copied ? "var(--font-dim)" : "black"}
    />

    const generateButton = <Button
        text={t("generate")}
        onClick={() => { setShowRecreatePopup(true) }}
        backgroundColor={"white"}
        textColor={"black"}
    />

    const key = data?.explyt_api_token
        ? <span className="api-key-span font-gray big-font"> {data.explyt_api_token} </span>
        : <div className="flex-center full-wide" style={{ padding: "3rem 0 3 rem 0" }}> <LoadingCircle className="loading-circle-big" /> </div>

    const tryOpenLink = (link: string | undefined) => {
        if (link === undefined) return;
        if (link.startsWith("https://t.me/") || link.startsWith("t.me/"))
            utils.openTelegramLink(link);
        else
            utils.openLink(link);
    }
    const goToSite = <Button
        text={t("downloadExplytTest")}
        onClick={() => tryOpenLink("https://explyt.com/en-eap/")}
        backgroundColor="var(--color-main)"
        textColor="black"
    />

    return <div className="small-padding-top">
        <div className="small-margin" style={{ height: "calc(var(--app-height) - 18rem)" }}>
            <div className="api-key-area">
                {key}
            </div>
            <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", marginTop: "2rem"}}>
                {generateButton}
                {copyButton}
            </div>
            {recreatePopup}
        </div>
        <div className="small-margin">
            {goToSite}
        </div>
    </div>
}